import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ShieldCheck, ArrowRight, Users, Gavel, Briefcase, Building2, Heart, Home, GraduationCap, UtensilsCrossed, Mail } from "lucide-react";

const PROGRAMS = [
  { icon: Home, title: "Family Housing Assistance", desc: "Helping families experiencing homelessness find stability, dignity and a place to call home." },
  { icon: GraduationCap, title: "Youth Mentorship", desc: "Mentoring and enrichment for at-risk youth ages 12–18, opening doors to brighter futures." },
  { icon: UtensilsCrossed, title: "Community Food Pantry", desc: "Nutritious food and essentials for low-income households across our community." },
];

const AUDIENCES = [
  { icon: Gavel, title: "Board Members", desc: "Review board packets, financial statements and strategic KPIs." },
  { icon: Building2, title: "Directors", desc: "Oversee funds, grants and program performance across the foundation." },
  { icon: Users, title: "Committee Members", desc: "Access governance records, resolutions and compliance calendars." },
  { icon: Briefcase, title: "Staff", desc: "Record transactions, manage donors, vendors and daily operations." },
];

const PRESETS = [5, 25, 50, 100, 250];
const CONTACT_EMAIL = "info@davidsharmonyhouse.org";

export default function Portal() {
  const navigate = useNavigate();
  const goLogin = () => navigate("/login");
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [thanksOpen, setThanksOpen] = useState(false);

  const selected = custom ? Number(custom) : amount;
  const donate = () => setThanksOpen(true); // TODO: swap for real checkout (Stripe/Zeffy) once payment method is connected

  return (
    <div className="min-h-screen bg-bone flex flex-col">
      {/* Top bar */}
      <header className="bg-forest text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
              <Heart className="h-5 w-5 text-white" fill="currentColor" />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-base font-semibold">David's Harmony House Foundation</div>
              <div className="text-[10px] tracking-widest text-forest-muted uppercase">Foundation Inc · IFMS v1.0</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="#donate" data-testid="nav-donate"
              className="bg-white text-forest hover:bg-stone-100 rounded-md px-4 py-2 text-sm font-semibold transition-colors duration-200 flex items-center gap-1.5">
              <Heart className="h-4 w-4" /> Donate
            </a>
            <button onClick={goLogin} data-testid="portal-signin-top"
              className="bg-white/10 hover:bg-white/20 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200 flex items-center gap-2">
              Member Sign In <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-forest text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07] bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1732783384071-0cdb7bbbad94?crop=entropy&cs=srgb&fm=jpg&q=85')" }} />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-24">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-forest-muted bg-white/5 border border-white/10 rounded-full px-3 py-1 mb-6">
            <ShieldCheck className="h-3.5 w-3.5" /> A 501(c)(3) nonprofit foundation
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] max-w-3xl">
            Integrated Financial<br />Management System (IFMS)
          </h1>
          <p className="text-forest-muted mt-6 max-w-2xl text-base leading-relaxed">
            David's Harmony House Foundation Inc. is a nonprofit dedicated to helping our neighbors find
            stability and hope — through housing assistance, youth mentorship and food security programs.
            Every gift makes a lasting difference.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a href="#donate" data-testid="hero-donate-cta"
              className="bg-white text-forest hover:bg-stone-100 rounded-md px-6 py-3 text-sm font-semibold transition-colors duration-200 flex items-center gap-2">
              <Heart className="h-4 w-4" /> Donate Now
            </a>
            <button onClick={goLogin} data-testid="portal-signin-cta"
              className="border border-white/25 hover:bg-white/10 text-white rounded-md px-6 py-3 text-sm font-semibold transition-colors duration-200 flex items-center gap-2">
              Member Sign In <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="max-w-2xl">
          <h2 className="font-heading text-3xl font-semibold text-[#1A1A1A]">What we do</h2>
          <p className="text-[#5C5C54] mt-3 leading-relaxed">
            Our programs meet families where they are — providing shelter, guidance and nourishment
            so every member of our community has the chance to thrive.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-5 mt-8">
          {PROGRAMS.map((p) => (
            <div key={p.title} data-testid={`program-${p.title.toLowerCase().split(" ")[0]}`}
              className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="h-11 w-11 rounded-lg bg-forest/8 flex items-center justify-center mb-4">
                <p.icon className="h-5 w-5 text-forest" />
              </div>
              <div className="font-heading text-lg text-[#1A1A1A]">{p.title}</div>
              <p className="text-sm text-[#5C5C54] mt-1.5 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Donate */}
      <section id="donate" className="bg-forest text-white scroll-mt-4">
        <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold leading-tight">Support our mission</h2>
            <p className="text-forest-muted mt-4 leading-relaxed max-w-md">
              Your generosity keeps families housed, youth mentored and pantry shelves full.
              Choose an amount below to make your gift to David's Harmony House Foundation Inc.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-7 text-[#1A1A1A]" data-testid="donate-card">
            <div className="font-heading text-xl mb-4">Make a donation</div>
            <div className="grid grid-cols-3 gap-2.5">
              {PRESETS.map((a) => (
                <button key={a} onClick={() => { setAmount(a); setCustom(""); }} data-testid={`donate-amount-${a}`}
                  className={`rounded-md py-2.5 text-sm font-semibold border transition-colors duration-200 ${
                    !custom && amount === a ? "bg-forest text-white border-forest" : "bg-white text-[#1A1A1A] border-stone-300 hover:border-forest"
                  }`}>
                  ${a}
                </button>
              ))}
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#8A9F95]">$</span>
                <input type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Other"
                  data-testid="donate-custom"
                  className={`w-full rounded-md py-2.5 pl-6 pr-2 text-sm border outline-none transition-colors duration-200 ${
                    custom ? "border-forest ring-2 ring-forest/15" : "border-stone-300 focus:border-forest"
                  }`} />
              </div>
            </div>
            <button onClick={donate} disabled={!selected || selected <= 0} data-testid="donate-submit"
              className="w-full mt-5 bg-forest hover:bg-forest-900 text-white rounded-md px-4 py-3 text-sm font-semibold transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60">
              <Heart className="h-4 w-4" /> Donate {selected > 0 ? `$${selected}` : ""}
            </button>
            <p className="text-[11px] text-[#8A9F95] text-center mt-3">Secure giving · Tax-deductible to the extent allowed by law</p>
          </div>
        </div>
      </section>

      {/* Member portal */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl font-semibold text-[#1A1A1A]">Member portal</h2>
            <p className="text-[#5C5C54] mt-3 leading-relaxed">
              Secure, by-invitation access for our Board, Directors, Committee members and Staff to the
              Integrated Financial Management System.
            </p>
          </div>
          <button onClick={goLogin} data-testid="portal-signin-section"
            className="shrink-0 bg-forest hover:bg-forest-900 text-white rounded-md px-5 py-2.5 text-sm font-semibold transition-colors duration-200 flex items-center gap-2">
            Sign In to the Portal <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {AUDIENCES.map((a) => (
            <div key={a.title} data-testid={`portal-audience-${a.title.toLowerCase().split(" ")[0]}`}
              className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="h-10 w-10 rounded-lg bg-forest/8 flex items-center justify-center mb-4">
                <a.icon className="h-5 w-5 text-forest" />
              </div>
              <div className="font-heading text-lg text-[#1A1A1A]">{a.title}</div>
              <p className="text-sm text-[#5C5C54] mt-1.5 leading-relaxed">{a.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-auto border-t border-stone-200 bg-bone">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5C5C54]">
          <span>David's Harmony House Foundation Inc · Integrated Financial Management System</span>
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-forest font-medium hover:underline flex items-center gap-1.5" data-testid="portal-contact">
            <Mail className="h-3.5 w-3.5" /> {CONTACT_EMAIL}
          </a>
        </div>
      </footer>

      {/* Donate placeholder dialog */}
      <Dialog open={thanksOpen} onOpenChange={setThanksOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="donate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2"><Heart className="h-5 w-5 text-forest" /> Thank you for your generosity</DialogTitle>
            <DialogDescription className="text-sm text-[#5C5C54] leading-relaxed pt-2">
              Online giving{selected > 0 ? ` (your ${`$${selected}`} gift)` : ""} is launching very soon. In the meantime,
              please reach out to arrange your donation and we'll make it easy.
            </DialogDescription>
          </DialogHeader>
          <a href={`mailto:${CONTACT_EMAIL}?subject=Donation to David's Harmony House Foundation`}
            data-testid="donate-dialog-contact"
            className="mt-2 w-full bg-forest hover:bg-forest-900 text-white rounded-md px-4 py-2.5 text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2">
            <Mail className="h-4 w-4" /> Contact us to donate
          </a>
        </DialogContent>
      </Dialog>
    </div>
  );
}
